import BankNoteIcon from "@uui/BankNote01";

import Typography from "@mui/material/Typography";

import BillingLink from "@app/analytics/account/Billing/components/BillingLink";
import BillingLinkButton from "@app/analytics/account/Billing/components/BillingLinkButton";
import BillingModuleError from "@app/analytics/account/Billing/components/BillingModuleError";
import {
  BillingCard,
  BillingCardActions,
  BillingCardContent,
  BillingCardHeader,
  CadenceOrTrialChip,
} from "@app/analytics/account/Billing/components/styles";
import useBillingCustomer from "@app/analytics/account/Billing/hooks/useBillingCustomer";
import {
  getCurrentPeriodEndDescription,
  isFreePlan,
} from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import { Client } from "@app/common/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { formatCurrencyFromCents } from "@app/utils/utils";

import Alert from "@rui/Alert";
import { Row } from "@rui/Stack";

import AdminCurrentPlanActions from "./AdminCurrentPlanActions";
import CancelPlanButton from "./CancelPlanButton";
import NonAdminCurrentPlanActions from "./NonAdminCurrentPlanActions";
import NonAdminCurrentPlanContent from "./NonAdminCurrentPlanContent";
import ReactivatePlanButton from "./ReactivatePlanButton";

const CurrentPlan = ({ client }: { client: Client }) => {
  const { inWorkspace, workspace } = useWorkspace();
  const { user } = useAuthenticated();
  const { canEditBillingPlan, canCreateWorkspace } = useFeatureAccess();
  const { billingCustomer } = useBillingCustomer();
  const {
    currentPlan,
    subscription,
    isTrial,
    plansInitialLoading,
    planLoadFailed,
    subscriptionIsError,
    scheduledPlan,
  } = useBillingContext();

  const workspaceOwner = workspace.users.find((u) => u.role === "owner")!;

  const subscriptionWillCancel =
    Boolean(subscription.cancelAt) || subscription.cancelAtPeriodEnd;
  const showCredits = (billingCustomer?.balance ?? 0) < 0;

  // whether the user can cancel or reactivate their subscription
  const canUpdatePlanStatus =
    canEditBillingPlan && !isTrial && !isFreePlan(currentPlan.type);

  return (
    <BillingCard testId="billing-current-plan">
      <BillingCardHeader>
        <Typography variant="bodyPrimaryMed">Current Plan</Typography>
        <BillingLink href={LINKS.MARKETING.PLANS_PRICING}>
          Compare plans
        </BillingLink>
      </BillingCardHeader>
      <BillingCardContent spacing={1}>
        {planLoadFailed ? (
          <BillingModuleError entityLabel="plan details" />
        ) : (
          <>
            {currentPlan.label && (
              <Row spacing={1}>
                <Typography variant="h3">{currentPlan.label}</Typography>
                <CadenceOrTrialChip
                  type={
                    subscription.inTrialPeriod
                      ? "free_trial"
                      : currentPlan.billingCadence
                  }
                />
              </Row>
            )}
            {inWorkspace && !canEditBillingPlan ? (
              <NonAdminCurrentPlanContent
                workspaceOwner={workspaceOwner}
                user={user}
              />
            ) : subscriptionIsError ? null : (
              <Typography
                variant="bodySecondaryReg"
                sx={{ color: "shades.neutral.600" }}
              >
                {getCurrentPeriodEndDescription({
                  currentPlan,
                  isTrial,
                  plansInitialLoading,
                  scheduledPlan,
                  subscription,
                })}
              </Typography>
            )}
            {showCredits && (
              <Alert
                embedded
                severity="success"
                icon={<BankNoteIcon />}
                sx={{ my: 1 }}
              >
                You have{" "}
                {formatCurrencyFromCents(Math.abs(billingCustomer!.balance))} in
                credits.
                {!subscriptionWillCancel &&
                  " This will automatically be applied to your next payment."}
              </Alert>
            )}
            {subscriptionIsError ? (
              <BillingModuleError
                entityLabel="plan details"
                sx={{ mb: "auto", mt: currentPlan.label ? 1 : 0 }}
              />
            ) : (
              <BillingCardActions spacing={1}>
                {inWorkspace ? (
                  canEditBillingPlan ? (
                    <AdminCurrentPlanActions client={client} />
                  ) : (
                    <NonAdminCurrentPlanActions client={client} />
                  )
                ) : (
                  <BillingLinkButton
                    to={ROUTES.BILLING_COMPARE_PLANS}
                    testId="billing-change-plan-button"
                  >
                    {isTrial
                      ? "Select plan"
                      : isFreePlan(currentPlan.type)
                        ? "Upgrade plan"
                        : "Change plan"}
                  </BillingLinkButton>
                )}
                {canCreateWorkspace && (
                  <BillingLinkButton
                    to={ROUTES.NEW_WORKSPACE}
                    variant="outlined"
                  >
                    Create a Workspace
                  </BillingLinkButton>
                )}
                {canUpdatePlanStatus &&
                  (subscriptionWillCancel ? (
                    <ReactivatePlanButton />
                  ) : (
                    <CancelPlanButton />
                  ))}
              </BillingCardActions>
            )}
          </>
        )}
      </BillingCardContent>
    </BillingCard>
  );
};

export default CurrentPlan;
